import * as React from "react";
import { useTranslation } from "react-i18next";
import { useMagneticCursor } from "senf-shared";
import { type VariantProps, tv } from "tailwind-variants";
import { v4 as uuidv4 } from "uuid";
import { cn } from "../../../util/classNameConstructor";
import Icon, { type IconProps } from "../icon/Icon";
import type { IconName } from "../icon/names";
import TooltipProvider, {
  type TooltipProviderProps,
} from "../tooltip/TooltipProvider";
import {
  bodyMdBold,
  bodyMdSemibold,
  bodySmBold,
  bodySmSemibold,
} from "../typography/Typography";

export const ButtonVariant = tv({
  base: `group/button focusable pointer-events-auto relative flex w-fit items-center justify-center gap-1 text-text-default hover:text-[var(--primary-1000)] active:text-[var(--primary-1000)] ${bodyMdSemibold}`,
  variants: {
    variant: {
      basic:
        "bg-action-secondary-default hover:bg-action-secondary-hovered active:bg-action-secondary-pressed",
      // primary: `focus-visible:outline-action-primary-pressed text-text-on-primary bg-action-primary-default hover:bg-action-primary-hovered active:bg-action-primary-pressed focus-visible:outline ${bodyMdBold}`,
      primary: `text-text-on-primary hover:text-text-on-primary  active:text-text-on-primary bg-action-primary-default hover:bg-action-primary-hovered active:bg-action-primary-pressed ${bodyMdBold}`,
      white: `bg-surface-default hover:bg-action-tertiary-hovered active:bg-action-tertiary-pressed hover:text-action-primary-depressed active:text-action-primary-depressed ${bodyMdBold}`,
      outline:
        "border-border-default hover:bg-action-secondary-hovered hover:border-border-hovered active:bg-action-secondary-pressed active:border-border-pressed border border-solid",
      shapeless:
        "hover:bg-action-secondary-hovered active:bg-action-secondary-pressed",
    },
    size: {
      xl: "h-14 px-6 py-4",
      lg: "h-11 px-[1.125rem] py-[0.6875rem]",
      md: "h-9 px-3.5 py-2",
      sm: "h-7 px-2.5 py-1",
    },
    iconOnly: {
      true: "aspect-square px-[initial]",
    },
    pillShaped: {
      true: "rounded-full",
      false: "rounded-xl",
    },
    disabled: {
      true: "pointer-events-none",
    },
    depressed: {
      true: "cursor-default",
    },
    critical: {
      true: `${bodyMdBold} hover:text-text-critical active:text-text-critical`,
    },
    width: {
      max: "w-full",
    },
    textSubdued: {
      true: "text-text-subdued hover:text-[var(--primary-800)] active:text-[var(--primary-800)]",
    },
  },
  compoundVariants: [
    // variant with state styles
    {
      variant: "basic",
      disabled: true,
      class:
        "bg-action-secondary-disabled text-text-disabled first:fill-text-disabled hover:bg-action-secondary-disabled",
    },
    {
      variant: "basic",
      disabled: true,
      depressed: true,
      class: "!bg-action-tertiary-depressed",
    },
    {
      variant: "basic",
      depressed: true,
      class:
        "bg-action-secondary-depressed hover:bg-action-secondary-depressed active:bg-action-secondary-depressed text-text-on-primary-depressed hover:text-text-on-primary-depressed first:fill-text-on-primary-depressed",
    },
    {
      variant: "basic",
      critical: true,
      class:
        "text-text-critical first:fill-icon-default bg-action-secondary-default hover:bg-surface-critical-subdued-hovered hover:text-text-critical hover:first:fill-icon-critical active:bg-surface-critical-subdued-pressed active:text-text-critical active:first:fill-icon-critical",
    },
    {
      variant: "basic",
      critical: true,
      depressed: true,
      class:
        "bg-action-critical-depressed hover:bg-action-critical-depressed active:bg-action-critical-depressed text-text-on-primary-depressed hover:text-text-on-primary-depressed active:text-text-on-primary-depressed first:fill-icon-on-primary-depressed",
    },
    {
      variant: "basic",
      critical: true,
      disabled: true,
      class:
        "bg-action-secondary-disabled text-action-critical-disabled first:fill-icon-disabled",
    },
    {
      variant: "shapeless",
      disabled: true,
      class: "text-text-disabled",
    },
    {
      variant: "shapeless",
      critical: true,
      class:
        "text-text-critical first:fill-icon-default hover:bg-surface-critical-subdued-hovered hover:text-text-critical hover:first:fill-icon-critical active:bg-surface-critical-subdued-pressed active:text-text-critical active:first:fill-icon-critical",
    },
    {
      variant: "shapeless",
      critical: true,
      depressed: true,
      class: "bg-transparent hover:bg-transparent active:bg-transparent",
    },
    {
      variant: "shapeless",
      critical: true,
      disabled: true,
      class:
        "text-action-critical-disabled first:fill-icon-disabled bg-transparent",
    },
    {
      variant: "primary",
      depressed: true,
      class:
        "text-text-on-primary-depressed bg-action-primary-depressed hover:bg-action-primary-depressed active:bg-action-primary-depressed",
    },
    {
      variant: "primary",
      disabled: true,
      class:
        "text-text-disabled bg-action-primary-disabled hover:bg-action-secondary-disabled active:bg-action-secondary-disabled",
    },
    {
      variant: "primary",
      critical: true,
      class:
        "text-text-on-critical hover:text-text-on-critical active:text-text-on-critical bg-action-critical-default hover:bg-action-critical-hovered active:bg-action-critical-pressed",
    },
    {
      variant: "primary",
      critical: true,
      depressed: true,
      class:
        "text-text-on-critical hover:text-text-on-critical active:text-text-on-critical bg-action-critical-depressed hover:bg-action-critical-depressed active:bg-action-critical-depressed",
    },
    {
      variant: "primary",
      critical: true,
      disabled: true,
      class: "bg-action-critical-disabled first:fill-text-on-critical",
    },
    {
      variant: "white",
      depressed: true,
      class:
        "text-text-on-critical bg-action-tertiary-depressed hover:text-text-depressed hover:bg-action-tertiary-depressed active:bg-action-tertiary-depressed",
    },
    {
      variant: "white",
      disabled: true,
      class: "bg-action-tertiary-disabled text-text-disabled",
    },
    {
      variant: "white",
      critical: true,
      class:
        "bg-surface-default text-text-critical hover:bg-surface-critical-subdued-hovered active:bg-surface-critical-subdued-pressed",
    },
    {
      variant: "white",
      depressed: true,
      critical: true,
      class:
        "text-action-critical-subdued hover:text-action-critical-subdued active:text-action-critical-subdued bg-surface-default hover:bg-surface-default active:bg-surface-default",
    },
    {
      variant: "white",
      disabled: true,
      critical: true,
      class: "bg-surface-default text-action-critical-disabled",
    },
    {
      variant: "outline",
      disabled: true,
      class: "border-border-disabled text-text-disabled",
    },
    {
      variant: "outline",
      depressed: true,
      class:
        "text-text-on-critical  border-border-depressed hover:border-border-depressed active:border-border-depressed bg-action-tertiary-depressed hover:text-text-depressed hover:bg-action-tertiary-depressed active:bg-action-tertiary-depressed",
    },
    {
      variant: "outline",
      critical: true,
      class:
        "text-text-critical first:fill-icon-critical border-border-critical-default hover:border-border-critical-default focus-visible:border-border-critical-default active:border-border-critical-default hover:bg-surface-critical-subdued-hovered active:bg-surface-critical-subdued-pressed bg-transparent",
    },
    {
      variant: "outline",
      disabled: true,
      depressed: true,
      class: "!bg-action-tertiary-depressed",
    },
    {
      variant: "outline",
      critical: true,
      depressed: true,
      class:
        "text-text-on-critical bg-action-critical-depressed hover:bg-action-critical-depressed active:bg-action-critical-depressed border-0",
    },
    {
      variant: "outline",
      critical: true,
      disabled: true,
      class: "border-border-disabled bg-transparent",
    },

    // iconOnly with size styles
    // @note: if iconOnly is true, padding should be equal on all sides to ensure a square button
    {
      size: "xl",
      iconOnly: true,
      class: "p-4",
    },
    {
      size: "lg",
      iconOnly: true,
      class: "p-2",
    },
    {
      size: "md",
      iconOnly: true,
      class: "p-1.5",
    },
    {
      size: "sm",
      iconOnly: true,
      class: "p-0.5",
    },

    // variant with sm size
    ...(["basic", "outline", "shapeless"] as const).map(
      (variant) =>
        ({
          size: "sm",
          variant,
          class: bodySmSemibold,
        }) as const,
    ),

    ...(["white", "primary"] as const).map(
      (variant) =>
        ({
          size: "sm",
          variant,
          class: bodySmBold,
        }) as const,
    ),

    {
      size: "sm",
      critical: true,
      class: bodySmBold,
    },
  ],
  defaultVariants: {
    variant: "basic",
    pillShaped: true,
    iconOnly: false,
    state: "default",
    size: "md",
  },
});

export interface ButtonProps
  extends Omit<VariantProps<typeof ButtonVariant>, "iconOnly">,
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "dir" | "onClick"> {
  text?: string | React.JSX.Element;
  "aria-label"?: string;
  leadingIcon?: IconName | Element;
  trailingIcon?: IconName | Element;
  leadingIconProps?: Partial<IconProps>;
  trailingIconProps?: Partial<IconProps>;
  loading?: boolean;
  depressed?: boolean;
  disabled?: boolean;
  critical?: boolean;
  textSubdued?: boolean;
  disabledNotice?: string;
  throttle?: number;
  trackId?: string;
  dataCy?: string;
  classNameWrapper?: string;
  tooltipLabel?: string;
  children?: React.ReactNode | React.ReactNode[];
  tag?: "button" | "a" | "span" | "div";
  tooltipProps?: Omit<TooltipProviderProps, "children">;
  onClick?: TooltipProviderProps["onClick"];
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      id,
      trackId,
      className,
      width,
      loading,
      leadingIcon,
      trailingIcon,
      leadingIconProps,
      trailingIconProps,
      pillShaped,
      onClick,
      dataCy,
      tooltipLabel,
      text,
      children,
      variant,
      disabled,
      depressed,
      critical,
      disabledNotice,
      classNameWrapper,
      role,
      size = "md",
      tag = "button",
      prefix,
      tooltipProps,
      textSubdued,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const buttonId = React.useRef(id || trackId || `tooltip-${uuidv4()}`);
    const reference = React.useRef<
      HTMLButtonElement | HTMLAnchorElement | HTMLDivElement | HTMLSpanElement
    >(null);
    // useMagneticCursor(reference, {
    //   strength: 0.2,
    //   radius: 100,
    //   enabled: magnetic && !disabled,
    //   smoothness: 0.15
    // });

    const leading = leadingIcon && loading ? "loading" : leadingIcon;
    const trailing =
      trailingIcon && !leadingIcon && loading ? "loading" : trailingIcon;
    const spinner = loading && !(leading || trailing);

    const handleKeyDown = React.useCallback(
      (
        event: React.KeyboardEvent<
          | HTMLButtonElement
          | HTMLAnchorElement
          | HTMLDivElement
          | HTMLSpanElement
        >,
      ) => {
        console.log("event", event.key);

        if (
          (event.key === "Enter" || event.key === " ") &&
          onClick &&
          !disabled
        ) {
          event.preventDefault();
          onClick(event as React.MouseEvent<HTMLButtonElement>);
        }
      },
      [onClick, disabled],
    );

    const Component = tag;
    const Item = (
      <Component
        data-cy={dataCy}
        id={buttonId.current}
        ref={ref || reference}
        type={tag === "button" ? "button" : undefined}
        role={tag !== "button" ? "button" : role}
        tabIndex={tag !== "button" ? 0 : undefined}
        onKeyDown={handleKeyDown}
        aria-label={
          tooltipLabel ?? (typeof text === "string" ? text : undefined)
        }
        aria-disabled={disabled}
        disabled={disabled}
        className={cn(
          ButtonVariant({
            depressed: !!depressed,
            disabled: !!disabled,
            critical: !!critical,
            size,
            iconOnly: !text && !children && !!(leadingIcon || trailingIcon),
            variant,
            pillShaped,
            width,
            textSubdued: !!textSubdued,
          }),
          "leading-none",
          className,
          trackId && `button umami--click--${trackId}`,
        )}
        {...props}
      >
        {prefix && (
          <span className="font-semibold text-text-subdued group-hover/button:text-[var(--primary-800)] group-active/button:text-[var(--primary-800)]">
            {prefix}
          </span>
        )}
        {leading && (
          <Icon
            {...leadingIconProps}
            name={leading}
            aria-hidden="true"
            className={cn(
              "shrink-0",
              variant === "primary" &&
                leading.toString().includes("logo/") &&
                "[&_path]:fill-current",
              text ? "-ml-1" : "",
              leadingIconProps?.className,

              typeof leading === "string" &&
                (leading?.includes("arrowhead-left") ||
                  leading?.includes("arrow-left")) &&
                document.dir === "rtl" &&
                "rotate-180",
            )}
          />
        )}
        {children}
        {spinner && (
          <Icon
            name="loading"
            className={cn("shrink-0", text ? "absolute" : "")}
          />
        )}
        {text && <span style={{ opacity: spinner ? 0 : 1 }}>{text}</span>}
        {trailing && (
          <Icon
            {...trailingIconProps}
            name={trailing}
            aria-hidden="true"
            className={cn(
              "shrink-0",
              variant === "primary" &&
                trailing.toString().includes("logo/") &&
                "[&_path]:fill-current",
              text ? "-mr-1 ml-auto" : "",
              trailingIconProps?.className,

              typeof trailing === "string" &&
                (trailing?.includes("arrowhead-right") ||
                  trailing?.includes("arrow-right")) &&
                document.dir === "rtl" &&
                "rotate-180",
            )}
          />
        )}
      </Component>
    );

    return (
      <TooltipProvider
        critical={!!disabled}
        disabled={disabled}
        primaryContent={disabled ? disabledNotice : tooltipLabel}
        noTooltip={!tooltipLabel && !disabled}
        onClick={onClick}
        className={cn(classNameWrapper, disabled && "cursor-not-allowed")}
        id={id}
        {...(tooltipProps || {})}
      >
        {Item}
      </TooltipProvider>
    );
  },
);

Button.displayName = "Button";
export default Button;
